import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { SubscriptionService } from '../../core/services';
import * as gtUtil from '../../core/utility';

const subscriptionService = new SubscriptionService();

type EntitlementKey = 'storefront_enabled' | 'link_in_bio_enabled';

interface Props {
    entitlementKey: EntitlementKey;
    children: React.ReactNode;
    // Where to send users whose plan doesn't include this feature.
    redirectTo?: string;
}

/**
 * Route-level guard: only renders `children` if the logged-in influencer's
 * ACTIVE plan entitlements include `entitlementKey`. Otherwise redirects to the
 * subscription/upgrade page. This closes the direct-URL bypass that the Zap hub
 * card lock alone can't prevent. Uses the same `getCurrentPlan` source the Zap
 * automations flow uses, so it always reflects plan + overrides.
 */
const EntitlementGuard: React.FC<Props> = ({ entitlementKey, children, redirectTo = '/influencer/settings/subscription' }) => {
    const user = useSelector((state: any) => state.UserReducer.UserData);
    const [status, setStatus] = useState<'checking' | 'allowed' | 'denied'>('checking');

    useEffect(() => {
        let cancelled = false;
        const check = async () => {
            if (!user?.influencer_id) return; // wait until the user is hydrated
            try {
                const res: any = await subscriptionService.getCurrentPlan({
                    account_id: user.account_id,
                    influencer_id: user.influencer_id,
                    user_id: user.user_id,
                    brand_id: 0,
                    user_type: 'Influencer',
                });
                const allowed =
                    res?.status === gtUtil.Constants.SUCCESS &&
                    !!res?.data?.entitlements?.[entitlementKey];
                if (!cancelled) setStatus(allowed ? 'allowed' : 'denied');
            } catch {
                if (!cancelled) setStatus('denied');
            }
        };
        check();
        return () => { cancelled = true; };
    }, [user?.influencer_id, user?.account_id, user?.user_id, entitlementKey]);

    if (status === 'denied') {
        return <Navigate to={redirectTo} replace />;
    }

    if (status === 'checking') {
        return (
            <Box sx={{ minHeight: 'calc(100vh - 70px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={28} sx={{ color: '#7c3aed' }} />
            </Box>
        );
    }

    return <>{children}</>;
};

export default EntitlementGuard;
